import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";

const VOYAGER = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const ATTRIB = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

function officeIcon(isHQ, signals, selected) {
  const color = isHQ ? "#0E9F6E" : "#2563EB";
  const size = isHQ ? 40 : Math.round(Math.max(28, Math.min(28 + (signals || 0) * 2, 42)));
  const inner = isHQ
    ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M12 2l2.9 6.1 6.6.6-5 4.4 1.5 6.5L12 16.9 6 19.6l1.5-6.5-5-4.4 6.6-.6z"/></svg>`
    : `<span style="color:#fff;font-weight:800;font-size:11px;font-family:ui-monospace,monospace;">${signals || ""}</span>`;
  const ring = selected
    ? `<span style="position:absolute;inset:-5px;border-radius:50%;border:3px solid ${color};box-shadow:0 0 0 3px rgba(255,255,255,.9);"></span>`
    : "";
  const html = `
    <div style="position:relative;width:${size}px;height:${size}px;cursor:pointer;">
      <span style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:${selected ? ".35" : ".22"};transform:scale(1.7);"></span>
      ${ring}
      <div style="position:relative;width:${size}px;height:${size}px;border-radius:50%;
        background:${color};border:2.5px solid #fff;box-shadow:0 3px 8px rgba(15,23,42,.35);
        display:flex;align-items:center;justify-content:center;">${inner}</div>
    </div>`;
  return L.divIcon({ html, className: "ik-office-marker", iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

export default function OfficesMiniMap({ offices, onSelect, selectedId }) {
  const pts = (offices || []).filter((o) => o.lat && o.lng);
  if (!pts.length) return null;

  const single = pts.length === 1;
  const center = single ? [pts[0].lat, pts[0].lng] : undefined;
  const bounds = single ? undefined : pts.map((o) => [o.lat, o.lng]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-border shadow-md" data-testid="offices-mini-map" style={{ height: 280 }}>
      <MapContainer
        center={center} zoom={single ? 11 : undefined}
        bounds={bounds} boundsOptions={{ padding: [40, 40], maxZoom: 11 }}
        scrollWheelZoom={false} style={{ height: "100%", width: "100%", background: "#e8ecf1" }}
      >
        <TileLayer url={VOYAGER} attribution={ATTRIB} subdomains="abcd" maxZoom={19} />
        {pts.map((o) => (
          <Marker
            key={o.id}
            position={[o.lat, o.lng]}
            icon={officeIcon(o.is_headquarters, o.signals, selectedId === o.id)}
            eventHandlers={onSelect ? { click: () => onSelect(selectedId === o.id ? null : o.id) } : undefined}
          >
            <Tooltip direction="top" offset={[0, -12]} opacity={1}>
              <span style={{ fontWeight: 700 }}>{o.name}</span>{o.is_headquarters ? " · Siège" : ""}
              {onSelect ? " — cliquez pour filtrer" : ""}
            </Tooltip>
            <Popup>
              <div style={{ minWidth: 170 }}>
                <div style={{ fontWeight: 700 }}>{o.name}</div>
                <div style={{ color: "#64748B", fontSize: 13 }}>
                  {[o.city, o.region].filter(Boolean).join(" · ")}
                </div>
                <div style={{ marginTop: 6, fontFamily: "monospace", fontSize: 12, color: o.is_headquarters ? "#0E9F6E" : "#2563EB", fontWeight: 700 }}>
                  {o.is_headquarters ? "Siège" : "Antenne"} · {o.signals || 0} signal{(o.signals || 0) > 1 ? "aux" : ""}
                </div>
                {onSelect && (
                  <button
                    onClick={() => onSelect(selectedId === o.id ? null : o.id)}
                    style={{ marginTop: 8, background: selectedId === o.id ? "#64748B" : "#2563EB", color: "#fff", border: 0, borderRadius: 8, padding: "6px 10px", fontWeight: 700, cursor: "pointer", fontSize: 11, width: "100%" }}
                  >
                    {selectedId === o.id ? "Retirer le filtre" : "Filtrer les signaux de cette zone"}
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <div className="absolute bottom-2.5 left-2.5 z-[500] bg-white/95 backdrop-blur rounded-lg shadow px-2.5 py-1.5 text-[11px] text-slate-700 font-medium" data-testid="offices-map-legend">
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#0E9F6E" }} /> Siège</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#2563EB" }} /> Antenne (taille = nb de besoins)</div>
      </div>
    </div>
  );
}
